import React, { useEffect, useMemo, useRef, useState } from "react";
import { HiOutlineX, HiOutlineChevronUp, HiOutlineChevronDown, HiOutlineDotsVertical, HiOutlineExclamation } from "react-icons/hi";
import { PiCheckBold, PiClockCounterClockwise } from "react-icons/pi";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";

import API from "../../api";
import { capture } from "../../instrument";
import { PRESCRIPTION_FLAG_LABELS, PRESCRIPTION_DOCTOR_STATUS_LABELS, isActRemovedByAction } from "../../utils";
import { getHiddenCodes } from "../../queries/hiddenCode";
import PrescriptionViewer from "../../components/PrescriptionViewer";
import { PrescriptionHoverProvider } from "../../components/PrescriptionHoverContext";
import ActChip from "../../components/ordonnance/ActChip";
import AddExamPopover from "../../components/ordonnance/AddExamPopover";
import ActExplanationPopover from "../../components/ordonnance/ActExplanationPopover";
import useRecordNavShortcuts from "../../hooks/useRecordNavShortcuts";

const WARNING_INSTRUCTIONS = {
  isHandwritten: "Vérifier l'intégration des textes manuscrits",
  hasExamWithoutCode: "Intégrer les éventuelles analyses manquantes (surlignées en rouge ou non surlignées)",
  hasAdditionalInfo: "Infos complémentaires (urgent, transmission, renouvellement, date de réalisation…)",
  hasRenewal: "Renouvellement",
  hasSchedule: "Date de réalisation",
  hasConditionNotTakenIntoAccount: "Que les conditions (si, +/-) soient respectées",
  hasCrossedOut: "Textes barrés",
};

const DOCTOR_STATUS_CHIP_CLASS = {
  FOUND: "border-green-200 bg-green-50 text-green-700",
  NOT_FOUND: "border-orange-200 bg-orange-50 text-orange-700",
  TOO_MANY_RESULTS: "border-amber-200 bg-amber-50 text-amber-700",
  NO_DOCTOR: "border-red-200 bg-red-50 text-red-700",
  DELETED: "border-red-200 bg-red-50 text-red-700",
  ERROR: "border-red-200 bg-red-50 text-red-700",
};

const FLAG_OPTIONS = [
  { flag: "MISSING_EXAM", chipClass: "border-red-200 bg-red-50 text-red-700" },
  { flag: "TRADUCTION_ERROR", chipClass: "border-red-200 bg-red-50 text-red-700" },
  { flag: "OK", chipClass: "border-green-200 bg-green-50 text-green-700", superAdminOnly: true },
];

const LEGEND = [
  { label: "Code ajouté", dotClass: "bg-yellow-400" },
  { label: "Code supprimé", dotClass: "bg-red-400" },
  { label: "Retiré par une action", dotClass: "bg-[repeating-linear-gradient(45deg,#ef4444_0_2px,transparent_2px_4px)]" },
  { label: "Code non trouvé", dotClass: "bg-orange-400" },
  { label: "Refusé par le SIL", dotClass: "bg-primary-400" },
  { label: "Inséré en ALD", dotClass: "bg-purple-400" },
  { label: "Inséré", dotClass: "bg-green-400" },
  { label: "Code masqué", dotClass: "bg-gray-400" },
];

const PrescriptionDetail = ({ user }) => {
  const history = useHistory();
  const { id } = useParams();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const [popoverAct, setPopoverAct] = useState(null);
  const [popoverAnchor, setPopoverAnchor] = useState(null);
  const [addedActIds, setAddedActIds] = useState(() => new Set());
  const [ignoredActIds, setIgnoredActIds] = useState(() => new Set());

  const filteredIds = location.state?.filteredIds || null;
  const currentIndex = useMemo(() => (filteredIds ? filteredIds.indexOf(id) : -1), [filteredIds, id]);
  const hasNavigation = filteredIds !== null && currentIndex !== -1;
  const isSuperAdmin = user.role === "superadmin";
  const canEditDictionary = user.role !== "user";
  const canOpenAuditLog = user.role !== "user";
  const isOverlayOpen = isMenuOpen || popoverAct !== null;

  const navigateTo = (index) => {
    setPopoverAct(null);
    setIsMenuOpen(false);
    history.replace({ pathname: `/prescription/${filteredIds[index]}`, state: { filteredIds } });
  };

  const navigatePrev = () => {
    if (!hasNavigation || currentIndex <= 0) return;
    navigateTo(currentIndex - 1);
  };

  const navigateNext = () => {
    if (!hasNavigation || currentIndex >= filteredIds.length - 1) return;
    navigateTo(currentIndex + 1);
  };

  const goBack = () => history.push("/prescription");

  useRecordNavShortcuts(
    {
      onPrev: navigatePrev,
      onNext: navigateNext,
      onBack: goBack,
      canPrev: hasNavigation && currentIndex > 0 && !isOverlayOpen,
      canNext: hasNavigation && currentIndex < filteredIds?.length - 1 && !isOverlayOpen,
      backEnabled: !isOverlayOpen,
    },
    [hasNavigation, currentIndex, filteredIds, isOverlayOpen],
  );

  const {
    data: prescription,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["prescription", id],
    queryFn: async () => {
      const response = await API.get(`/prescription/${id}`);
      if (!response.ok) throw new Error(response.message);
      return response.data;
    },
  });

  const { data: hiddenCodes } = useQuery({ queryKey: ["hidden-codes"], queryFn: getHiddenCodes });
  const hiddenCodeByValue = useMemo(() => new Map((hiddenCodes || []).map((hiddenCode) => [hiddenCode.code, hiddenCode])), [hiddenCodes]);

  useEffect(() => {
    if (!isError) return;
    toast.error("Une erreur est survenue lors du chargement de l'ordonnance");
    history.push("/prescription");
  }, [isError]);

  useEffect(() => {
    if (!isMenuOpen) return;
    const onClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) setIsMenuOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [isMenuOpen]);

  const flagMutation = useMutation({
    mutationFn: (flag) => API.put(`/prescription/${id}/flag`, { flag }),
    onSuccess: (response) => {
      if (!response.ok) return toast.error(response.message || "Une erreur est survenue");
      queryClient.setQueryData(["prescription", id], (previous) => ({ ...previous, flags: response.data.flags }));
      toast.success("Signalement mis à jour");
    },
    onError: (error) => {
      capture(error);
      toast.error("Une erreur est survenue");
    },
  });

  const polygons = useMemo(() => {
    if (!prescription) return [];
    return prescription.acts
      .filter((act) => act.polygon.length)
      .map((act) => {
        const codes = act.isDeleted ? act.codesPredicted : act.codes;
        const isHidden = codes.length > 0 && codes.every((code) => hiddenCodeByValue.has(code));
        return {
          id: act._id,
          points: act.polygon,
          className: `querco_polygon_${act._id} ${
            isHidden
              ? "fill-gray-400 stroke-gray-400"
              : act.codes.length > 0 && act.codesPredicted.length === 0
                ? "fill-yellow-500 stroke-yellow-500"
                : act.isDeleted && act.codesPredicted?.length !== 0
                  ? "fill-red-500 stroke-red-500"
                  : isActRemovedByAction(act)
                    ? "fill-[url(#action-removed-stripes)] stroke-red-500"
                    : act.codesPredicted.length === 0
                      ? "fill-orange-500 stroke-orange-500"
                      : act.notInserted && act.codesPredicted.length !== 0
                        ? "fill-primary-500 stroke-primary-500"
                        : act.ALD
                          ? "fill-purple-500 stroke-purple-500"
                          : "fill-green-500 stroke-green-500"
          } opacity-25 stroke-1`,
          ALD: act.ALD,
          isDeleted: act.isDeleted && act.codesPredicted?.length !== 0,
          codeNotFound: act.codesPredicted.length === 0,
          codeNotInserted: act.notInserted && act.codesPredicted.length !== 0,
          codes: [act._id],
          pageNumber: act.pageNumber,
        };
      });
  }, [prescription, hiddenCodeByValue]);

  const headerPolygons = useMemo(() => {
    if (!prescription || popoverAct?.examTypeOffset === undefined) return [];
    const header = prescription.ner?.find((entity) => entity.category === "ExamType" && entity.offset === popoverAct.examTypeOffset);
    if (!header) return [];
    return header.polygons.map((polygon, index) => ({
      id: `header_${header._id}_${index}`,
      points: polygon.map((point) => ({ x: point.X, y: point.Y })),
      className: "fill-primary-500 stroke-primary-500 opacity-25 stroke-1",
      codes: [],
      pageNumber: header.pageNumber,
    }));
  }, [prescription, popoverAct]);

  const viewerPolygons = useMemo(() => [...polygons, ...headerPolygons], [polygons, headerPolygons]);

  const activeWarnings = prescription ? Object.keys(WARNING_INSTRUCTIONS).filter((key) => prescription.warning?.[key]) : [];
  const activeFlags = prescription ? FLAG_OPTIONS.filter((option) => prescription.flags?.includes(option.flag)) : [];
  const visibleFlagOptions = FLAG_OPTIONS.filter((option) => !option.superAdminOnly || isSuperAdmin);
  const doctorStatus = prescription?.doctor?.status;
  const displayedActs = prescription ? prescription.acts.filter((act) => act.text || (act.isDeleted ? act.codesPredicted : act.codes).length > 0) : [];
  const uncodedActs = prescription
    ? prescription.acts.filter((act) => act.codesPredicted.length === 0 && act.text && !addedActIds.has(act._id) && !ignoredActIds.has(act._id))
    : [];
  const isAddExamPopover = popoverAct !== null && canEditDictionary && uncodedActs.some((act) => act._id === popoverAct._id);
  const canHideCodes = canEditDictionary && prescription?.indexName === user.indexName;

  const onExamAdded = (actId) => {
    setAddedActIds((previous) => new Set([...previous, actId]));
    setPopoverAct(null);
  };

  const onTermIgnored = (actId) => {
    setIgnoredActIds((previous) => new Set([...previous, actId]));
    setPopoverAct(null);
  };

  return (
    <div className="flex h-[calc(100vh-60px)] flex-col bg-gray-50">
      {hasNavigation && (
        <div className="flex items-center gap-1.5 border-b border-gray-200 bg-white px-3 py-1.5">
          <button type="button" className="rounded-md p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-600" onClick={goBack} title="Fermer (Esc)">
            <HiOutlineX className="h-4 w-4" />
          </button>
          <div className="mx-0.5 h-4 w-px bg-gray-200" />
          <button
            type="button"
            className="rounded-md p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-gray-500"
            onClick={navigatePrev}
            disabled={currentIndex <= 0}
          >
            <HiOutlineChevronUp className="h-4 w-4" />
          </button>
          <button
            type="button"
            className="rounded-md p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-gray-500"
            onClick={navigateNext}
            disabled={currentIndex >= filteredIds.length - 1}
          >
            <HiOutlineChevronDown className="h-4 w-4" />
          </button>
          <span className="ml-1 text-xs tabular-nums text-gray-500">
            {currentIndex + 1} / {filteredIds.length}
          </span>
        </div>
      )}

      <div className="flex items-start justify-between border-b border-gray-200 bg-white px-8 pb-5 pt-6">
        <div className="flex min-w-0 items-center gap-3">
          <h1 className="truncate text-lg font-semibold text-gray-900">{prescription?.silAdmissionNumber || prescription?.type || "Ordonnance"}</h1>
          {prescription?.silAdmissionNumber && (
            <span className="inline-flex shrink-0 items-center rounded-md border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs font-medium text-gray-600">
              {prescription.type}
            </span>
          )}
          {activeFlags.map((option) => (
            <span key={option.flag} className={`inline-flex shrink-0 items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-medium ${option.chipClass}`}>
              {option.flag === "OK" ? <PiCheckBold className="h-3 w-3" /> : <HiOutlineExclamation className="h-3 w-3" />}
              {PRESCRIPTION_FLAG_LABELS[option.flag]}
            </span>
          ))}
        </div>
        <div className="flex shrink-0 items-center gap-1.5 pl-4">
          <div className="relative" ref={menuRef}>
            <button
              type="button"
              className="rounded-md p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-600"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              disabled={!prescription}
            >
              <HiOutlineDotsVertical className="h-5 w-5" />
            </button>
            {isMenuOpen && (
              <div className="absolute right-0 z-10 mt-1 w-56 rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
                {canOpenAuditLog && (
                  <>
                    <button
                      type="button"
                      className="flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50"
                      onClick={() => {
                        setIsMenuOpen(false);
                        window.open(`/audit-log/prescriptions/${id}`, "_blank", "noopener,noreferrer,width=1000,height=800");
                      }}
                    >
                      <PiClockCounterClockwise className="h-3.5 w-3.5 shrink-0" />
                      Historique
                    </button>
                    <div className="my-1 border-t border-gray-100" />
                  </>
                )}
                <div className="px-3 pb-1 pt-1.5 text-[11px] font-medium uppercase tracking-wide text-gray-400">Signaler</div>
                {visibleFlagOptions.map((option) => {
                  const isFlagged = prescription.flags?.includes(option.flag);
                  return (
                    <button
                      key={option.flag}
                      type="button"
                      className="flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50"
                      disabled={flagMutation.isPending}
                      onClick={() => {
                        setIsMenuOpen(false);
                        flagMutation.mutate(option.flag);
                      }}
                    >
                      <PiCheckBold className={`h-3.5 w-3.5 shrink-0 ${isFlagged ? "text-primary-500" : "text-transparent"}`} />
                      {PRESCRIPTION_FLAG_LABELS[option.flag]}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {isLoading || !prescription ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-primary-500" />
          <span className="text-sm text-gray-400">Chargement...</span>
        </div>
      ) : (
        <PrescriptionHoverProvider>
          <div className="grid flex-1 grid-cols-5 overflow-hidden">
            <div className="col-span-3 flex min-w-0 flex-col overflow-hidden">
              <div className="flex flex-1 justify-center overflow-auto p-6">
                <PrescriptionViewer id={prescription._id} imgUrl={prescription.img} polygons={viewerPolygons} heightRatio={0.72} />
              </div>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-gray-200 bg-white px-6 py-2 text-[11px] text-gray-500">
                {LEGEND.map((item) => (
                  <span key={item.label} className="inline-flex items-center gap-1.5">
                    <span className={`h-2 w-2 rounded-full ${item.dotClass}`} />
                    {item.label}
                  </span>
                ))}
              </div>
            </div>

            <div className="col-span-2 overflow-y-auto border-l border-gray-200 bg-white p-6">
              {activeWarnings.length > 0 && (
                <div className="mb-6 flex items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                  <HiOutlineExclamation className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-800">Vérifications</p>
                    <ul className="mt-1 list-disc space-y-0.5 pl-4 text-sm text-gray-600">
                      {activeWarnings.map((key) => (
                        <li key={key}>{WARNING_INSTRUCTIONS[key]}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              <h3 className="mb-4 text-lg font-medium text-gray-900">Détails</h3>
              <div className="divide-y divide-gray-100 rounded-lg border border-gray-100 bg-white">
                <div className="flex items-center gap-4 px-5 py-3">
                  <span className="w-40 shrink-0 text-[13px] font-medium text-gray-600">Reçue le</span>
                  <span className="min-w-0 flex-1 truncate text-sm text-gray-900">
                    {new Date(prescription.createdAt).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}
                  </span>
                </div>
                <div className="flex items-center gap-4 px-5 py-3">
                  <span className="w-40 shrink-0 text-[13px] font-medium text-gray-600">Date de prescription</span>
                  <span className="min-w-0 flex-1 truncate text-sm text-gray-900">{prescription.date || <span className="text-gray-400">N/A</span>}</span>
                </div>
                <div className="flex items-center gap-4 px-5 py-3">
                  <span className="w-40 shrink-0 text-[13px] font-medium text-gray-600">RPPS</span>
                  <span className="min-w-0 flex-1 truncate text-sm tabular-nums text-gray-900">{prescription.doctor?.RPPS || <span className="text-gray-400">N/A</span>}</span>
                </div>
                <div className="flex items-center gap-4 px-5 py-3">
                  <span className="w-40 shrink-0 text-[13px] font-medium text-gray-600">ADELI</span>
                  <span className="min-w-0 flex-1 truncate text-sm tabular-nums text-gray-900">{prescription.doctor?.ADELI || <span className="text-gray-400">N/A</span>}</span>
                </div>
                {doctorStatus && (
                  <div className="flex items-center gap-4 px-5 py-3">
                    <span className="w-40 shrink-0 text-[13px] font-medium text-gray-600">Code prescripteur</span>
                    <span className="flex min-w-0 flex-1 items-center gap-2">
                      <span className="truncate text-sm text-gray-900">{prescription.doctor?.code || <span className="text-gray-400">N/A</span>}</span>
                      <span
                        className={`inline-flex shrink-0 items-center rounded-md border px-2 py-0.5 text-xs font-medium ${DOCTOR_STATUS_CHIP_CLASS[doctorStatus] || "border-gray-200 bg-gray-50 text-gray-600"}`}
                      >
                        {PRESCRIPTION_DOCTOR_STATUS_LABELS[doctorStatus] || doctorStatus}
                      </span>
                    </span>
                  </div>
                )}
              </div>

              <div className="mb-4 mt-8 flex items-baseline gap-2">
                <h3 className="text-lg font-medium text-gray-900">Examens</h3>
                <span className="text-sm tabular-nums text-gray-400">{displayedActs.length}</span>
                <span className="text-xs text-gray-400">
                  {canEditDictionary && uncodedActs.length > 0
                    ? "Cliquez un examen pour voir d'où vient son code, un examen sans code pour l'ajouter au dictionnaire"
                    : "Cliquez un examen pour voir d'où vient son code"}
                </span>
              </div>
              {displayedActs.length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                  {displayedActs.map((act) => (
                    <ActChip
                      key={act._id}
                      act={act}
                      hiddenCodes={hiddenCodeByValue}
                      doneLabel={
                        addedActIds.has(act._id)
                          ? "Ajouté au dictionnaire, pris en compte aux prochaines extractions"
                          : ignoredActIds.has(act._id)
                            ? "Terme ignoré aux prochaines extractions"
                            : undefined
                      }
                      isActive={popoverAct?._id === act._id}
                      onClick={(event) => {
                        setPopoverAnchor(event.currentTarget);
                        setPopoverAct(popoverAct?._id === act._id ? null : act);
                      }}
                    />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400">Aucun examen détecté</p>
              )}
              {isAddExamPopover && (
                <AddExamPopover act={popoverAct} referenceElement={popoverAnchor} onClose={() => setPopoverAct(null)} onAdded={onExamAdded} onIgnored={onTermIgnored} />
              )}
              {popoverAct && !isAddExamPopover && (
                <ActExplanationPopover
                  act={popoverAct}
                  prescription={prescription}
                  referenceElement={popoverAnchor}
                  canOpenConfiguration={canEditDictionary}
                  hiddenCodes={hiddenCodeByValue}
                  canHideCodes={canHideCodes}
                  onReportError={() => {
                    flagMutation.mutate("TRADUCTION_ERROR");
                    setPopoverAct(null);
                  }}
                  onClose={() => setPopoverAct(null)}
                />
              )}

              {isSuperAdmin && prescription.ner?.length > 0 && (
                <>
                  <h3 className="mb-4 mt-8 text-lg font-medium text-gray-900">NER</h3>
                  <div className="divide-y divide-gray-100 rounded-lg border border-gray-100 bg-white">
                    {prescription.ner.map((ner) => (
                      <div key={ner._id} className="flex items-start gap-4 px-5 py-2.5">
                        <span className="w-40 shrink-0 text-[13px] font-medium text-gray-600">{ner.category}</span>
                        <span className="min-w-0 flex-1 break-words text-sm text-gray-900">{ner.content}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </PrescriptionHoverProvider>
      )}
    </div>
  );
};

export default PrescriptionDetail;
